/**
 * Sparkline — pure SVG, no deps. Normalizes points to [0..1] and draws a
 * filled gradient + stroked path. Defaults to green positive; red for
 * `positive={false}`.
 */
type Props = {
	points: number[];
	width?: number;
	height?: number;
	positive?: boolean;
};

export function Sparkline({
	points,
	width = 120,
	height = 36,
	positive = true,
}: Props) {
	if (!points || points.length === 0) {
		return <svg width={width} height={height} aria-hidden="true" />;
	}

	const stroke = positive ? "#16a34a" : "#dc2626";
	const gradId = positive ? "cfSparkGradPos" : "cfSparkGradNeg";
	const fillTopOpacity = 0.24;

	const max = Math.max(...points);
	const min = Math.min(...points);
	const range = max - min || 1;
	const stepX = points.length > 1 ? width / (points.length - 1) : width;

	const coords = points.map((v, i) => {
		const x = i * stepX;
		const y = height - ((v - min) / range) * (height - 2) - 1;
		return { x, y };
	});

	const linePath = coords
		.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
		.join(" ");

	const areaPath = `${linePath} L ${coords[coords.length - 1].x} ${height} L ${coords[0].x} ${height} Z`;

	return (
		<svg
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			aria-hidden="true"
			className="block"
		>
			<defs>
				<linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
					<stop
						offset="0%"
						stopColor={positive ? "#22c55e" : "#ef4444"}
						stopOpacity={fillTopOpacity}
					/>
					<stop
						offset="100%"
						stopColor={positive ? "#22c55e" : "#ef4444"}
						stopOpacity={0}
					/>
				</linearGradient>
			</defs>
			<path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
			<path
				d={linePath}
				stroke={stroke}
				strokeWidth={1.5}
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}
